<template>
	
	<div v-if="layout.dataset" class="w-full h-full flex flex-col text-gray-900 px-6 pb-4">

		<div class="flex-1 flex flex-col overflow-hidden">

			<div v-if="!layout.loading" class="flex flex-col h-full">
				
				<!-- Header section with filters and buttons -->
				<div class="flex-shrink-0">
					<div class="box py-1 shadow-none rounded">
						<div class="flex items-center">

							<input 
								type="text" 
								v-model="layout.tagFilter" 
								@input="filterTags()" 
								placeholder="Filter tag name..." 
								class="rp-input-text w-2/5"/>

							<div v-if="layout.dataset.type !== 'ANOMALY'" class="ml-auto flex space-x-2">
								
								<button 
									type="button"
									class="rp-btn-action-primary"
									@click="layout.combine = true">
									<PlusIcon class="w-4 h-4 mr-1" />
									{{ $t('Combine tags') }}
								</button>

								<button 
									type="button"
									class="rp-btn-action-primary"
									@click="layout.create = true">
									<PlusIcon class="w-4 h-4 mr-1" />
									{{ $t('New tag type') }}
								</button>
								
							</div>

						</div>
					</div>
				</div>

				<!-- Table section that grows with content -->
				<div class="flex-shrink-0 mt-4">
					<div id="tabulator" ref="tableRef" class="table-report table-report--tabulator bg-white tabulatorTags"></div>
				</div>

			</div>

			<div v-else class="intro-y flex flex-col col-span-12 items-center mt-5">
				<LoadingIcon icon="three-dots" class="w-12 h-12" />
			</div>

		</div>

		<CreateTagModal v-if="layout.create" 
			interface="tags" 
			:dataset="layout.dataset"
            @closeModal="layout.create = false"
			@tagCreated="reload()"/>

		<EditTagModal v-if="layout.edit" 
			:dataset="layout.dataset"
			:tagID="layout.edit"
            @closeModal="layout.edit = false"
			@tagEdited="reload()"/>

		<CombineTagModal v-if="layout.combine" 
			:dataset="layout.dataset"
            @closeModal="layout.combine = false"
			@tagsCombined="reload()"/>

	</div>

</template>

<script setup>
import { onMounted, onUnmounted, ref } from "vue";

import * as rosepetalModel  from "rosepetal-model";

import CreateTagModal 	from "@/modals/tag/CreateTag.vue";
import EditTagModal 	from "@/modals/tag/EditTag.vue";
import CombineTagModal 	from "@/modals/tag/CombineTag.vue";

const $emit = defineEmits(['updateDataset']);

let $dataset	= rosepetalModel.default.dataset;
let $table  	= rosepetalModel.default.table;

const props = defineProps({
    dataset: {
		type: Object,
		required: true
	}
});

const layout = ref({
	dataset:     		false,
	loading:     		false,
	tabulator:   		false,
	edit:        		false,
	create:      		false,
	combine:	 		false,
	tagFilter:  		'',
	newTagName:  		'',
	combineTargetTag: 	'',
	currentTag:			{},
	combineTags: 		[],
	confirmDelete: 		false,
	confirmDeleteInput: ''
});

const tableRef = ref(false);

const loadTable = async (tagsFiltered = {}) => { 
	const tableFields = ['Name', 'Images', 'Color'];
	const tableTags = layout.value.tagFilter ? tagsFiltered : layout.value.dataset.tags;

	const tableData = Object.entries(tableTags).map(([id, tag]) => ({
		id,
		name:	tag.name,
		images: layout.value.dataset.tagStats.tags[id],
		color: 	tag.color,
	}));

	const tableColumns = tableFields.map(field => ({
		title: 		field,
		field: 		field.toLowerCase(),
		hozAlign: 	field === "Name" ? "left" : "center",
		headerHozAlign: "center",
		resizable: 	false,
		formatter: 	field === "Color"
			? cell => `<div class="w-20 h-4 rounded-full mx-auto" style="background-color: ${cell.getValue()}"></div>`
			: cell => `<div class="text-xs text-gray-700 whitespace-nowrap">${cell.getValue()}</div>`
	}));

	const handleRowClick = (_, row) => { layout.value.edit = row.getData().id; }

	layout.value.tabulator = await $table.init(tableRef.value, tableData, tableColumns, handleRowClick, {
		pagination: true,
		paginationSize: 10,
		paginationSizeSelector: [5, 10, 15, 20],
		paginationCounter: "rows",
		layout: "fitColumns"
	});
}

const loadDataset = async (reload = false) => { 
	layout.value.loading = true;

	if (reload || !layout.value.dataset.tagStats) {
		layout.value.dataset.tags 		= await $dataset.getTags(props.dataset.id);
		layout.value.dataset.tagStats 	= await $dataset.getTagStats(props.dataset);

		const storedData = JSON.parse(sessionStorage.getItem('datasetData') || '{}');

		storedData.tags			= layout.value.dataset.tags;
		storedData.tagStats 	= layout.value.dataset.tagStats;

		sessionStorage.setItem('datasetData', JSON.stringify(storedData));
	}
	
	layout.value.loading = false;
}

const filterTags = async () => {
	const tagsFiltered = Object.entries(layout.value.dataset.tags).reduce((acc, [id, tag]) => {
		if (tag.name.toLowerCase().includes(layout.value.tagFilter.toLowerCase())) {
			acc[id] = tag;
		}
		return acc;
	}, {});

	await loadTable(tagsFiltered);
}

const reload = async () => {
	layout.value.create 	= false;
	layout.value.edit 		= false;
	layout.value.combine	= false;

	await loadDataset(true);
	await loadTable();

	$emit("updateDataset", "tags");
}

onMounted(async () => { 
	console.log("------dataset-tags mounted------");

	if (props.dataset) {
		layout.value.dataset = props.dataset;
		await loadDataset(); 
		await loadTable();
	}
});

onUnmounted(() => { 
	console.log("------dataset-tags unmounted------")
});
</script>

<style>
.table-rp-tags td { 
	padding: 3px 5px !important; 
}

.tabulatorTags .tabulator-header { 
	border-top-left-radius: 5px; 
	border-top-right-radius: 5px;
	border-right: 3px solid #f7f8f9 !important; 
	border-left: 3px solid #f7f8f9 !important; 
}

.tabulatorTags .tabulator-header .tabulator-headers .tabulator-col { 
	background-color: #fff !important; 
	color: #333 !important; 
}

.tabulatorTags .tabulator-header .tabulator-headers .tabulator-col:hover { 
	background-color: #17356e !important; 
	color: #fff !important; 
}

.tabulatorTags .tabulator-footer .tabulator-paginator > label { 
	display: none !important; 
}

.tabulatorTags .tabulator-footer .tabulator-paginator .tabulator-page-size { 
	margin-left: 0 !important; 
}

.tabulatorTags .tabulator-footer { 
	display: flex !important;
	justify-content: center !important;
	align-items: center !important;
	padding: 0.75rem !important;
}

.tabulatorTags .tabulator-row .tabulator-cell { 
	padding: 10px 15px !important; 
	height: 40px !important; 
	text-align: left; 
}

.tabulatorTags .tabulator-row.tabulator-row-even { 
	--tw-bg-opacity: 1; 
	background-color: #f7fafc !important; 
}

.tabulatorTags .tabulator-header { 
	border-right: 1px solid #f7f8f9 !important; 
	border-left: 1px solid #f7f8f9 !important;
	width: 100% !important; 
	overflow: hidden !important; 
}

.sliderBox .vc-slider-swatches { 
	margin-top: 8px !important; 
}
</style>